import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges
} from '@angular/core'
import {
  BookingCateringFormValue,
  BookingMultiDayForm
} from 'src/app/shared/models'
import moment from 'moment-mini'
import { Moment } from 'moment-mini'
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { AppState } from '../../../../core/core.state'
import { Store } from '@ngrx/store'
import { productSelectors } from '../../../product/product.selectors'
import { Observable } from 'rxjs'
import { Product } from '../../../product/product'
import { saleSelectors } from 'src/app/features/sale/sale.selectors'
import { ConfigurationService } from '../../../../shared/services/configuration.service'
import { BookingMultiDayService } from '../booking-multi-day.service'
import { markFormGroupDirty } from '../../../../shared/utils/mark-form-dirty'
import { RequestQueryBuilder, CondOperator } from '@nestjsx/crud-request'
import { productActions } from 'src/app/features/product/product.actions'
import { map } from 'rxjs/operators'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { scrollToTop } from 'src/app/shared/until'

@UntilDestroy()
@Component({
  selector: 'step-04-catering-form',
  templateUrl: './step04-catering-form.component.html',
  styleUrls: ['./step04-catering-form.component.scss']
})
export class Step04CateringFormComponent implements OnInit, OnChanges {
  @Input() datesList: Moment[]
  @Input() products: Partial<Product>[]
  @Input() loading: boolean = false
  @Output() onSubmit = new EventEmitter<BookingCateringFormValue>()
  @Input() bookingCatering: any
  @Input() booking: BookingMultiDayForm

  public form: UntypedFormGroup
  public isLoading$: Observable<boolean>
  public selectedDate: Moment
  public selectedDateIndex: number = 0
  public bookableProducts: Product[]
  public productEntities$ = this.store.select(productSelectors.selectEntities)

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store<AppState>,
    public configurationService: ConfigurationService,
    public bookingMultiDayService: BookingMultiDayService
  ) {}

  public get datesForm(): UntypedFormArray {
    return this.form?.get('dates') as UntypedFormArray
  }

  public getDatesForm(selectedDate: Moment): UntypedFormGroup {
    let formGroup: UntypedFormGroup
    this.datesForm.controls.forEach((control: UntypedFormGroup) => {
      if (control.value['date'] === selectedDate?.format?.()) {
        formGroup = control
      }
    })
    return formGroup
  }

  public getParentBookableProducts(product: Product): Observable<Product[]> {
    return this.productEntities$.pipe(
      map((productEntities) =>
        product.parentBookableProducts
          .map((pId) => productEntities[pId])
          .sort((a, b) => a.description.localeCompare(b.description))
      )
    )
  }

  public getProductForm(
    productId: number,
    selectedDate: Moment
  ): UntypedFormGroup {
    let result: UntypedFormGroup
    this.datesForm.controls.forEach((control: UntypedFormGroup) => {
      if (control.value['date'] === selectedDate?.format?.()) {
        let formArray = control.controls['products'] as UntypedFormArray

        formArray.controls.forEach((nestedControl: UntypedFormGroup) => {
          if (nestedControl.value['productId'] === productId) {
            result = nestedControl
          }
        })
      }
    })

    return result
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.products &&
      JSON.stringify(changes.products?.currentValue) !==
        JSON.stringify(changes.products?.previousValue)
    ) {
      this.products = changes.products.currentValue.sort((a, b) =>
        a.description.localeCompare(b.description)
      )
      console.log('selected products for the step ', this.products)
    }
  }
  ngOnInit(): void {
    this.isLoading$ = this.store.select(saleSelectors.selectIsLoading)

    this.store
      .select(productSelectors.selectAll)
      .pipe(untilDestroyed(this))
      .subscribe(
        (products) =>
          (this.bookableProducts = products.filter((p) => p.bookable))
      )

    // set first date as selected
    this.selectedDate = this.datesList?.[0]

    this.form = this.fb.group({
      dates: new UntypedFormArray([])
    })

    // create a form group for each date based on product
    this.datesList.forEach((date) => {
      this.addDatesForm(date)
    })
  }

  onSubmitFormValues(event: Event) {
    if (this.form.invalid) {
      markFormGroupDirty(this.form)
      return
    }
    this.onSubmit.emit(this.form.value)
  }

  public copyFromLastDay(dateIndex: number): void {
    const lastDay = dateIndex - 1
    if (lastDay >= 0) {
      const currentValue = this.getDatesForm(this.datesList[dateIndex]).value
      const previousDayFormValue = this.getDatesForm(
        this.datesList[lastDay]
      ).value

      this.getDatesForm(this.datesList[dateIndex]).setValue({
        ...previousDayFormValue,
        date: currentValue.date
      })
    }
  }

  wholeStayStatus: boolean = false
  setForWholeStay(dateIndex: number) {
    this.wholeStayStatus = !this.wholeStayStatus
    if (this.wholeStayStatus) {
      const formValue = this.getDatesForm(this.datesList[dateIndex]).value

      this.datesList.forEach((date, index) => {
        if (dateIndex !== index) {
          this.getDatesForm(this.datesList[index]).setValue({
            ...formValue,
            date: date.format()
          })
        }
      })
    }
  }

  public addDateBefore() {
    const firstDate: Moment = moment(this.datesList[0])
    firstDate.subtract(1, 'days')
    this.datesList.unshift(firstDate)

    this.addDatesForm(firstDate)
    this.selectedDate = this.datesList[0]
  }

  public addDateAfter() {
    const lastDate: Moment = moment(this.datesList[this.datesList.length - 1])
    lastDate.add(1, 'days')
    this.datesList.push(lastDate)

    this.addDatesForm(lastDate)
    this.selectedDate = this.datesList[this.datesList.length - 1]
  }

  public removeDate(dateIndex: number) {
    const selectedDate = this.datesList[dateIndex]
    this.datesList.splice(dateIndex, 1)
    this.removeDatesFormByDate(selectedDate)
    this.selectedDate = this.datesList[0]
  }

  public removeDatesFormByDate(selectedDate: Moment): void {
    this.datesForm.controls.forEach(
      (control: UntypedFormGroup, index: number) => {
        if (control.value['date'] === selectedDate?.format?.()) {
          this.datesForm.removeAt(index)
        }
      }
    )
  }

  public disabledHours(): number[] {
    return [0, 1, 2, 3, 4, 5, 6, 21, 22, 23]
  }

  public disabledMinutes(hour: number): number[] {
    return hour === 20 ? Array.from({ length: 29 }, (_, i) => i + 31) : []
  }

  private addDatesForm(date: Moment) {
    const fg = this.fb.group({
      date: [date.format()],
      products: new UntypedFormArray([])
    })

    const productFormGroup = fg.get('products') as UntypedFormArray

    this.products.forEach((product) => {
      // product.parentBookableProducts.forEach((productId) => {

      let defaultCheckInTime
      if (
        product.defaultStartTime &&
        typeof product.defaultStartTime == 'string' &&
        !moment(product.defaultStartTime).isValid()
      ) {
        defaultCheckInTime = product.defaultStartTime
          ? moment('2020-01-01 ' + product.defaultStartTime, 'YYYY-MM-DD HH:mm')
          : null
      } else if (
        product.defaultStartTime &&
        typeof product.defaultStartTime !== 'string' &&
        moment(product.defaultStartTime).isValid()
      ) {
        defaultCheckInTime = product.defaultStartTime
          ? moment(
              '2020-01-01 ' +
                moment(moment(product.defaultStartTime).toDate()).format(
                  'HH:mm'
                ),
              'YYYY-MM-DD HH:mm'
            )
          : null
      } else if (
        product.defaultStartTime &&
        typeof product.defaultStartTime == 'string' &&
        moment(product.defaultStartTime).isValid()
      ) {
        defaultCheckInTime = moment(
          moment(product.defaultStartTime).toDate(),
          'YYYY-MM-DD HH:mm'
        )
      } else {
        defaultCheckInTime = null
      }

      const config = {
        productId: [product.id],
        bookableProductId: [null, []],
        time: [defaultCheckInTime, []],
        qty: [null, [Validators.min(1)]],
        option: [null],
        selected: [null]
      }
      const pfg = this.fb.group(config)
      // {
      //   validators: (formGroup: FormGroup) => {
      //     if (formGroup.controls.selected.value === true) {
      //       formGroup.controls.time.validator = Validators.required
      //       formGroup.controls.option.validator = Validators.required
      //     }
      //   }
      // }
      productFormGroup.push(pfg)
      // })
    })

    this.datesForm.push(fg)
  }
  onDateClick(date, index) {
    this.selectedDateIndex = index
    this.selectedDate = date
  }
  public onSearchProducts(term: string) {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          }
        })
      })
    )
  }
}
